import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AxiosResponse } from 'axios';
import {
  BillingTransition,
  deleteBillingClientRecipient,
  deleteBillingServiceCorrection,
  getBillingContractPatients,
  getBillingServiceCorrections,
  postBillingServiceCorrection,
  getBillingClientRecipients,
  getBillingClients,
  getBillingContracts,
  getBillingContractVersions,
  getBillingFormulas,
  getBillingPatientIdFields,
  getBillingSettlement,
  getBillingSettlementChecks,
  getBillingSettlementDispatches,
  getBillingSettlementDocuments,
  getBillingSettlementReviewLinks,
  getBillingSettlements,
  postBillingClientRecipient,
  postBillingContract,
  postBillingContractVersion,
  postBillingSettlementDispatch,
  postBillingSettlementDraft,
  postBillingSettlementEvidence,
  postBillingSettlementImport,
  postBillingSettlementTransition,
  putBillingContractAutoDraft,
  putBillingContractIssueOnValidation,
  putBillingContractVersionPrice,
} from 'services/api';
import {
  BillingClient,
  BillingContract,
  BillingFormula,
  BillingError,
  ClientRecipient,
  ContractPatient,
  ContractVersion,
  DispatchPurpose,
  NewContract,
  PatientIdFieldOption,
  ReviewLink,
  Settlement,
  SettlementCheck,
  SettlementDispatch,
  SettlementDocument,
  ServiceCorrection,
} from './interface';

/**
 * Los endpoints de facturación responden 200 con success=false en los errores de dominio. `billingThunk` los convierte
 * en un rechazo con el errorCode, para que la pantalla explique el motivo (los toasts genéricos están silenciados para
 * 'billing/' en errorNotificationMiddleware). Un error HTTP o de red también llega como BillingError.
 */
const billingThunk = <T, A = void>(type: string, call: (arg: A) => Promise<AxiosResponse>) =>
  createAsyncThunk<T, A, { rejectValue: BillingError }>(type, async (arg, { rejectWithValue }) => {
    try {
      const response = await call(arg);
      if (response?.data?.success) {
        return response.data.item as T;
      }
      return rejectWithValue({ errorCode: response?.data?.errorCode, message: response?.data?.message });
    } catch (error: any) {
      const data = error?.response?.data;
      return rejectWithValue({ errorCode: data?.errorCode, message: data?.message ?? error?.message });
    }
  });

export const getBillingContractsAsync = billingThunk<BillingContract[]>('billing/getContracts', () =>
  getBillingContracts(),
);

export const getBillingSettlementsAsync = billingThunk<Settlement[], string>('billing/getSettlements', (contractId) =>
  getBillingSettlements(contractId),
);

export const getBillingSettlementAsync = billingThunk<Settlement, string>('billing/getSettlement', (settlementId) =>
  getBillingSettlement(settlementId),
);

export const calculateBillingDraftAsync = billingThunk<Settlement, { contractId: string; year: number; month: number }>(
  'billing/calculateDraft',
  (a) => postBillingSettlementDraft(a.contractId, a.year, a.month),
);

export const importBillingSettlementAsync = billingThunk<
  Settlement,
  { contractId: string; year: number; month: number; note: string }
>('billing/importIssued', (a) => postBillingSettlementImport(a.contractId, a.year, a.month, a.note));

export const transitionBillingSettlementAsync = billingThunk<
  Settlement,
  { settlementId: string; transition: BillingTransition; body?: Record<string, string | undefined> }
>('billing/transition', (a) => postBillingSettlementTransition(a.settlementId, a.transition, a.body));

export const dispatchBillingSettlementAsync = billingThunk<
  SettlementDispatch,
  { settlementId: string; purpose: DispatchPurpose }
>('billing/dispatch', (a) => postBillingSettlementDispatch(a.settlementId, a.purpose));

export const getBillingDispatchesAsync = billingThunk<SettlementDispatch[], string>('billing/getDispatches', (id) =>
  getBillingSettlementDispatches(id),
);

export const getBillingReviewLinksAsync = billingThunk<ReviewLink[], string>('billing/getReviewLinks', (id) =>
  getBillingSettlementReviewLinks(id),
);

export const getBillingDocumentsAsync = billingThunk<SettlementDocument[], string>('billing/getDocuments', (id) =>
  getBillingSettlementDocuments(id),
);

export const uploadBillingEvidenceAsync = billingThunk<SettlementDocument, { settlementId: string; file: File }>(
  'billing/uploadEvidence',
  (a) => postBillingSettlementEvidence(a.settlementId, a.file),
);

export const getBillingRecipientsAsync = billingThunk<ClientRecipient[], string>('billing/getRecipients', (clientId) =>
  getBillingClientRecipients(clientId),
);

export const addBillingRecipientAsync = billingThunk<
  ClientRecipient,
  { clientId: string; purpose: DispatchPurpose; name?: string; email: string }
>('billing/addRecipient', (a) =>
  postBillingClientRecipient(a.clientId, { purpose: a.purpose, name: a.name, email: a.email }),
);

export const removeBillingRecipientAsync = billingThunk<boolean, { clientId: string; recipientId: string }>(
  'billing/removeRecipient',
  (a) => deleteBillingClientRecipient(a.clientId, a.recipientId),
);

export const getBillingChecksAsync = billingThunk<SettlementCheck[], string>('billing/getChecks', (settlementId) =>
  getBillingSettlementChecks(settlementId),
);

export const updateBillingAutoDraftAsync = billingThunk<
  number | null,
  { contractId: string; autoDraftDay: number | null }
>('billing/updateAutoDraft', (a) => putBillingContractAutoDraft(a.contractId, a.autoDraftDay));

export const updateBillingIssueOnValidationAsync = billingThunk<boolean, { contractId: string; enabled: boolean }>(
  'billing/updateIssueOnValidation',
  (a) => putBillingContractIssueOnValidation(a.contractId, a.enabled),
);

export const getBillingCorrectionsAsync = billingThunk<ServiceCorrection[], string>(
  'billing/getCorrections',
  (contractId) => getBillingServiceCorrections(contractId),
);

export const getBillingContractPatientsAsync = billingThunk<ContractPatient[], string>(
  'billing/getContractPatients',
  (contractId) => getBillingContractPatients(contractId),
);

export const createBillingCorrectionAsync = billingThunk<
  ServiceCorrection,
  { contractId: string; patientId: string; serviceStart?: string; serviceEnd?: string; reason: string }
>('billing/createCorrection', ({ contractId, ...correction }) => postBillingServiceCorrection(contractId, correction));

export const deleteBillingCorrectionAsync = billingThunk<boolean, { contractId: string; correctionId: string }>(
  'billing/deleteCorrection',
  (a) => deleteBillingServiceCorrection(a.contractId, a.correctionId),
);

export const getBillingVersionsAsync = billingThunk<ContractVersion[], string>('billing/getVersions', (contractId) =>
  getBillingContractVersions(contractId),
);

export const updateBillingVersionPriceAsync = billingThunk<
  ContractVersion[],
  { contractId: string; versionId: string; unitPrice: number | null }
>('billing/updateVersionPrice', (a) => putBillingContractVersionPrice(a.contractId, a.versionId, a.unitPrice));

export const createBillingVersionAsync = billingThunk<
  ContractVersion[],
  { contractId: string; fromYear: number; fromMonth: number; unitPrice: number }
>('billing/createVersion', (a) => postBillingContractVersion(a.contractId, a.fromYear, a.fromMonth, a.unitPrice));

export const getBillingClientsAsync = billingThunk<BillingClient[]>('billing/getClients', () => getBillingClients());

export const getBillingFormulasAsync = billingThunk<BillingFormula[]>('billing/getFormulas', () =>
  getBillingFormulas(),
);

export const createBillingContractAsync = billingThunk<BillingContract, NewContract>('billing/createContract', (c) =>
  postBillingContract(c),
);

export const getBillingPatientIdFieldsAsync = billingThunk<PatientIdFieldOption[], string | null>(
  'billing/getPatientIdFields',
  (groupId) => getBillingPatientIdFields(groupId),
);
